import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Users, MapPin, Heart, ArrowLeft, MessageCircle } from "lucide-react";
import { Header } from "@/components/Header";
import { EventCard } from "@/components/EventCard";
import { API, apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const when = (iso) =>
  new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function Communities() {
  const { slug } = useParams();
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = () => {
    if (slug) apiFetch(`/communities/${slug}`).then(setDetail).catch(() => setDetail(false));
    else apiFetch("/communities").then((r) => setList(r.communities)).catch(() => {});
  };

  useEffect(load, [slug]);

  const follow = async (targetSlug, name) => {
    try {
      const res = await apiFetch(`/communities/${targetSlug}/follow`, { method: "POST" });
      toast.success(res.following ? `Following ${name}` : `Unfollowed ${name}`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (slug) {
    if (detail === false) return <div className="p-10">Community not found.</div>;
    if (!detail) return <div className="min-h-screen bg-white" />;
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <Link
            to="/communities"
            data-testid="communities-back"
            className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 transition-colors duration-200 hover:text-neutral-900"
          >
            <ArrowLeft className="h-4 w-4" /> All communities
          </Link>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl" data-testid="community-name">
                {detail.name}
              </h1>
              <p className="mt-2 flex flex-wrap items-center gap-x-4 text-sm text-neutral-600">
                <span>{detail.upcoming} upcoming · {detail.total} dates on the calendar</span>
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" /> {detail.follower_count} following
                </span>
              </p>
              {detail.lead_name ? (
                <p className="mt-3 rounded-xl bg-neutral-50 px-4 py-3 text-sm" data-testid="community-lead">
                  <span className="font-bold text-neutral-900">{detail.lead_name}</span>
                  {detail.lead_title ? <span className="text-neutral-600"> · {detail.lead_title} · </span> : " · "}
                  {detail.lead_email ? (
                    <a href={`mailto:${detail.lead_email}`} className="ml-2 font-semibold text-[#4D7C0F] hover:underline">
                      {detail.lead_email}
                    </a>
                  ) : null}
                  {detail.lead_url ? (
                    <a href={detail.lead_url} target="_blank" rel="noreferrer" className="ml-2 font-semibold text-[#4D7C0F] hover:underline">
                      link
                    </a>
                  ) : null}
                  {detail.lead_user_id ? (
                    <span className="mt-2 flex flex-wrap items-center gap-3">
                      <Link
                        to={`/members/${detail.lead_user_id}`}
                        data-testid="community-lead-profile"
                        className="font-semibold text-[#4D7C0F] hover:underline"
                      >
                        View profile
                      </Link>
                      {detail.lead_user_id !== me?.user_id && (
                      <Link
                        to={`/messages?to=${detail.lead_user_id}`}
                        data-testid="community-lead-message"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#B8FF2E] px-3 py-1.5 text-xs font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
                      >
                        <MessageCircle className="h-3.5 w-3.5" /> Message {detail.lead_name.split(" ")[0]}
                      </Link>
                      )}
                    </span>
                  ) : null}
                </p>
              ) : null}
              {detail.leaders.length > 0 && (
                <p className="mt-2 text-sm text-neutral-600" data-testid="community-leaders">
                  <span className="font-semibold text-neutral-800">Leaders:</span> {detail.leaders.join(" · ")}
                </p>
              )}
              {detail.leaders.length === 0 && !detail.lead_name && (
                <p className="mt-2 text-sm text-neutral-500" data-testid="community-no-leaders">
                  No named leader yet — hosts show up here once listings include a point of contact.
                </p>
              )}
              {detail.venues.length > 0 && (
                <p className="mt-1 flex items-center gap-1 text-sm text-neutral-500">
                  <MapPin className="h-3.5 w-3.5" /> {detail.venues.join(" · ")}
                </p>
              )}
            </div>
            <button
              onClick={() => follow(detail.slug, detail.name)}
              data-testid="community-follow-btn"
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${
                detail.following
                  ? "border border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-100"
                  : "bg-[#B8FF2E] text-[#14210A] hover:bg-[#A2E80B]"
              }`}
            >
              <Heart className={`h-4 w-4 ${detail.following ? "fill-[#84CC16] text-[#4D7C0F]" : ""}`} />
              {detail.following ? "Following" : "Follow community"}
            </button>
          </div>

          <h2 className="mt-12 text-2xl font-bold tracking-tight text-neutral-900">Next dates</h2>
          {detail.upcoming_events.length === 0 ? (
            <p className="mt-3 text-sm text-neutral-500">Nothing scheduled right now.</p>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="community-events">
              {detail.upcoming_events.map((e, i) => (
                <EventCard key={e.event_id} event={e} index={i} />
              ))}
            </div>
          )}

          {detail.photos.length > 0 && (
            <>
              <h2 className="mt-14 text-2xl font-bold tracking-tight text-neutral-900">From past nights</h2>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="community-photos">
                {detail.photos.map((p) => (
                  <Link
                    key={p.photo_id}
                    to={`/events/${p.event_id}`}
                    className="overflow-hidden rounded-xl bg-neutral-100 aspect-square"
                  >
                    <img
                      src={`${API}/photos/${p.photo_id}/image`}
                      alt={p.caption || p.event_title}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Communities</h1>
        <p className="mt-2 text-sm text-neutral-500 md:text-lg">
          Every group running events on the calendar. Follow the ones you care about and their dates lead your Monday email.
        </p>

        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="communities-grid">
          {list.map((c) => (
            <li key={c.slug} className="rounded-2xl border border-neutral-200 p-5" data-testid={`community-${c.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <Link to={`/communities/${c.slug}`} className="min-w-0">
                  <p className="truncate text-base font-bold text-neutral-900">{c.name}</p>
                  <p className="mt-1 text-xs text-neutral-500">
                    {c.upcoming} upcoming · {c.follower_count} following
                  </p>
                </Link>
                <button
                  onClick={() => follow(c.slug, c.name)}
                  data-testid={`follow-${c.slug}`}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition-colors duration-200 ${
                    c.following
                      ? "border border-neutral-900 text-neutral-900 hover:bg-neutral-100"
                      : "bg-neutral-900 text-white hover:bg-neutral-700"
                  }`}
                >
                  {c.following ? "Following" : "Follow"}
                </button>
              </div>
              {c.next_event && (
                <Link
                  to={`/events/${c.next_event.event_id}`}
                  className="mt-4 block rounded-xl bg-neutral-50 p-3 transition-colors duration-200 hover:bg-neutral-100"
                >
                  <p className="truncate text-sm font-semibold text-neutral-900">{c.next_event.title}</p>
                  <p className="mt-0.5 text-xs text-neutral-500">
                    {when(c.next_event.start_at)} · {c.next_event.venue_name}
                  </p>
                </Link>
              )}
              {c.lead_name ? (
                <p className="mt-3 truncate text-xs font-semibold text-neutral-700" data-testid={`lead-${c.slug}`}>
                  Host: {c.lead_name}
                  {c.lead_title ? ` · ${c.lead_title}` : ""}
                </p>
              ) : c.leaders.length > 0 ? (
                <p className="mt-3 truncate text-xs text-neutral-500">Led by {c.leaders.slice(0, 2).join(", ")}</p>
              ) : null}            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
